import { useMutation } from "@apollo/client"
import { Avatar, Box, Card, CardActionArea, Divider, Stack, Typography } from "@mui/material"
import Grid from "@mui/material/Grid2"
import React, { useContext, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { CenterCircular } from "../../components/Loading"
import { UserContext } from "../../contexts/UserContext"
import { MANAGE_DRIVER_ASSIGNMENT } from "../../entities/users/graphql/mutations"
import { IVehicle } from "../../entities/vehicle/interface"
import SiteContext from "../../contexts/SiteContext"
import dayjs from "dayjs"

// Site component
const Sites = () => {
    const { t } = useTranslation()
    const [vehicles, setVehicles] = useState<IVehicle[]>([])
    const [isVehicle, setIsVehicle] = useState<boolean>(false)
    const siteContext = useContext(SiteContext)
    const navigate = useNavigate()
    const userContext = useContext(UserContext)
    const [manageDriverAssignment, { loading: creatingShifting }] = useMutation(MANAGE_DRIVER_ASSIGNMENT)
    const timeZone = localStorage.getItem("siteTimezone") || "UTC"
    const date = dayjs().tz(timeZone).format("YYYY-MM-DD")

    if (!userContext) return

    const handleAllowedVehicles = async (site: any) => {
        siteContext?.setSiteId(site.site._id)
        setIsVehicle(true)
        if (site?.siteDriverDetails?.allowedVehicles?.length > 0) {
            setVehicles(site.siteDriverDetails.allowedVehicles)
        }
    }
    const VehicleDetails: React.FC<{ title: string; subTitle: string }> = ({ title, subTitle }) => (
        <Box>
            <Typography variant="body2" color="white" sx={{ fontWeight: "medium", fontSize: "1.1rem" }}>
                {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                {subTitle}
            </Typography>
        </Box>
    )

    const handleSelectVehicle = async (vehicle: any) => {
        // We need to list the shift templates, so that driver can select the shift template while creating a new shift

        //for now default shift template will be override/created for the vehicle with the current driver

        await manageDriverAssignment({
            variables: {
                assignment: {
                    driverId: userContext?.user?._id,
                    vehicleId: vehicle._id,
                    shiftDate: date,
                },
            },
        })

        userContext?.setIsUserUpdate(true)
        navigate("/")
    }
    const colors = ["primary.main", "secondary.light", "primary.orange"] // Array of three colors
    return (
        <Box
            width="100%"
            paddingTop="3vh" // Adjusting top padding for better alignment
            display="flex"
            flexDirection="column"
            alignItems="center"
            sx={{ height: "100vh" }} // Dark background for contrast
            overflow={"auto"} // Enable scrolling
        >
            {creatingShifting && <CenterCircular />}

            {isVehicle ? (
                <Stack sx={{ pl: 1, pr: 3, width: "90%" }} spacing={4}>
                    <Typography
                        color="text.primary"
                        variant="h6"
                        sx={{
                            textAlign: "center",
                            mb: 3,
                        }}
                    >
                        {t("shifts.mobile.buttons.start")}
                    </Typography>

                    {vehicles && vehicles?.length > 0 ? (
                        vehicles.map((vehicle, index) => (
                            <Box
                                key={index} // Use job.id if available, fallback to index
                                sx={{
                                    width: "100%",
                                    marginTop: { xs: 3, sm: 4, md: 5 },
                                    position: "relative",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "right",
                                }}
                                onClick={() => handleSelectVehicle(vehicle)} // Wrap in a function
                            >
                                <Box
                                    width="100%"
                                    position="absolute"
                                    top="-25px"
                                    right={1}
                                    display="flex"
                                    justifyContent="end"
                                >
                                    <Avatar
                                        sx={{
                                            boxShadow: 10,
                                            backgroundColor: "white",
                                            height: { xs: "55px", sm: "75px", md: "90px" },
                                            width: { xs: "55px", sm: "75px", md: "90px" },
                                            position: "relative",
                                        }}
                                    >
                                        <Typography variant="h4" color="secondary.main">
                                            {vehicle?.shift?.length ?? 0}
                                        </Typography>
                                    </Avatar>
                                </Box>
                                <Box
                                    width="100%"
                                    display="flex"
                                    flexDirection="row"
                                    borderRadius={5}
                                    sx={{ bgcolor: colors[index % colors.length] }} // Assign color based on index
                                    p={2}
                                >
                                    <Box width="90%" position="relative" marginY={1}>
                                        <CardActionArea>
                                            <Box padding={2}>
                                                <VehicleDetails
                                                    title={vehicle.identifier}
                                                    subTitle={`${vehicle.capacity}T`}
                                                />
                                                <Divider
                                                    sx={{
                                                        width: "20%",
                                                        borderStyle: "solid",
                                                        marginTop: "6%",
                                                        marginBottom: "6%",
                                                        borderColor: "white",
                                                    }}
                                                />
                                                <VehicleDetails
                                                    title={vehicle.licencePlate}
                                                    subTitle={vehicle.engineNumber}
                                                />
                                            </Box>
                                        </CardActionArea>
                                    </Box>
                                </Box>
                            </Box>
                        ))
                    ) : (
                        <Typography variant="body1" color="text.secondary" sx={{ mt: 3 }}>
                            {t("vehicles.mobile.noVehicle")}
                        </Typography>
                    )}
                </Stack>
            ) : (
                <Stack sx={{ pl: 1, pr: 3, width: "90%" }} spacing={4}>
                    <Typography
                        color="text.primary"
                        variant="h6"
                        sx={{
                            textAlign: "center",
                            mb: 3,
                        }}
                    >
                        {t("sites.mobile.selectSite")}
                    </Typography>

                    <Grid container spacing={2} justifyContent="center">
                        {userContext?.user?.sites && userContext?.user?.sites.length > 0 ? (
                            userContext?.user?.sites.map((site, index) => (
                                <Grid size={{ xs: 12, sm: 12, md: 4 }} key={index}>
                                    <Card
                                        sx={{
                                            cursor: "pointer",
                                            borderRadius: "12px",
                                            backgroundColor: "primary.main",
                                            p: 2,
                                        }}
                                        onClick={() => handleAllowedVehicles(site)} // Wrap in a function
                                    >
                                        <Typography
                                            variant="h6"
                                            color="text.primary"
                                            sx={{
                                                textAlign: "center",
                                            }}
                                        >
                                            {site.site.name}
                                        </Typography>
                                    </Card>
                                </Grid>
                            ))
                        ) : (
                            <Typography variant="body1" color="text.secondary" sx={{ mt: 3 }}>
                                {t("sites.mobile.noSiteAvailable")}
                            </Typography>
                        )}
                    </Grid>
                </Stack>
            )}
        </Box>
    )
}

export default Sites
